import { rgb } from "./acts";

/**
 * The score for MONOLITH. Same seven sections as the particle piece, but the
 * subject is architecture rather than dust, so what gets interpolated is camera
 * placement, material response, atmosphere and which distance field is on
 * screen.
 *
 * `world` indexes the branch in the raymarch shader.
 */

export const MONOLITH_ACTS = [
  {
    id: "genesis",
    kicker: "00 / arrival",
    world: 0,
    camera: { pos: [0, 2, 40], target: [0, 4, 0], fov: 50 },
    look: {
      surface: rgb("#8d95a3"),
      accent: rgb("#ff9a4a"),
      sky: rgb("#0a0d16"),
      skyHigh: rgb("#161d2e"),
      fog: rgb("#0a0d16"),
      fogDensity: 0.014,
      light: [0.45, 0.55, 0.4],
      glow: 0.75,
      rough: 0.55,
      kaleido: 0,
    },
    grade: { bloom: 1.05, aberration: 0.75, exposure: 1.1, vignette: 0.85 },
  },
  {
    id: "identity",
    kicker: "01 / who",
    world: 1,
    camera: { pos: [0, 1.5, 30], target: [0, 5, -10], fov: 46 },
    decal: "name",
    decalPos: [0, 6.5, -16],
    decalSize: [30, 12],
    look: {
      surface: rgb("#c8c4ba"),
      accent: rgb("#8fd8ff"),
      sky: rgb("#070b14"),
      skyHigh: rgb("#101a2c"),
      fog: rgb("#070b14"),
      fogDensity: 0.012,
      light: [-0.3, 0.6, 0.55],
      glow: 0.5,
      rough: 0.35,
      kaleido: 0,
    },
    grade: { bloom: 0.95, aberration: 0.4, exposure: 1.08, vignette: 0.78 },
  },
  {
    id: "currently",
    kicker: "02 / currently",
    world: 2,
    camera: { pos: [20, 9, 24], target: [0, 3, 0], fov: 52 },
    look: {
      surface: rgb("#3d444c"),
      accent: rgb("#4ff0c0"),
      sky: rgb("#040c12"),
      skyHigh: rgb("#0a2028"),
      fog: rgb("#04090e"),
      fogDensity: 0.016,
      light: [0.5, 0.7, 0.2],
      glow: 1.0,
      rough: 0.25,
      kaleido: 0,
    },
    grade: { bloom: 1.25, aberration: 0.65, exposure: 1.1, vignette: 0.8 },
  },
  {
    id: "past",
    kicker: "03 / past life",
    world: 3,
    camera: { pos: [0, 0, 14], target: [0, 1, -30], fov: 74 },
    look: {
      surface: rgb("#a8886a"),
      accent: rgb("#ffb04a"),
      sky: rgb("#0d0805"),
      skyHigh: rgb("#241408"),
      fog: rgb("#0d0805"),
      fogDensity: 0.026,
      light: [0.1, 0.5, -0.85],
      glow: 1.1,
      rough: 0.6,
      kaleido: 0,
    },
    grade: { bloom: 1.35, aberration: 1.1, exposure: 1.12, vignette: 0.95 },
  },
  {
    id: "projects",
    kicker: "04 / work",
    world: 4,
    camera: { pos: [0, 17, 38], target: [0, 1, 0], fov: 48 },
    look: {
      surface: rgb("#5a4478"),
      accent: rgb("#d79bff"),
      sky: rgb("#0a0614"),
      skyHigh: rgb("#1c1030"),
      fog: rgb("#0a0614"),
      fogDensity: 0.013,
      light: [0.35, 0.75, 0.5],
      glow: 0.9,
      rough: 0.2,
      kaleido: 0,
    },
    grade: { bloom: 1.2, aberration: 0.55, exposure: 1.1, vignette: 0.8 },
  },
  {
    id: "skills",
    kicker: "05 / toolkit",
    world: 5,
    camera: { pos: [0, 7, 46], target: [0, 2, 0], fov: 50 },
    look: {
      surface: rgb("#2b2f3a"),
      accent: rgb("#ffd36e"),
      sky: rgb("#05070d"),
      skyHigh: rgb("#141024"),
      fog: rgb("#05070d"),
      fogDensity: 0.02,
      light: [0.6, 0.4, 0.6],
      glow: 0.8,
      rough: 0.15,
      kaleido: 0.35,
    },
    grade: { bloom: 1.0, aberration: 1.1, exposure: 1.0, vignette: 0.88 },
  },
  {
    id: "contact",
    kicker: "06 / signal",
    world: 6,
    camera: { pos: [0, 2, 30], target: [0, 3.5, -8], fov: 48 },
    decal: "build",
    decalPos: [0, 5, -30],
    decalSize: [26, 13],
    look: {
      surface: rgb("#d6d2c8"),
      accent: rgb("#ffeccb"),
      sky: rgb("#080b10"),
      skyHigh: rgb("#1a2230"),
      fog: rgb("#080b10"),
      fogDensity: 0.011,
      light: [0.0, 0.45, -0.9],
      glow: 1.15,
      rough: 0.3,
      kaleido: 0,
    },
    grade: { bloom: 1.15, aberration: 0.45, exposure: 1.12, vignette: 0.76 },
  },
];

/** Ring the five project solids orbit on. Mirrors `orbitCenter` in the shader. */
export const ORBIT_RADIUS = 15;
export const orbitCenter = (i, t) => {
  const a = (i / 6) * Math.PI * 2 + t * 0.16;
  return [
    Math.cos(a) * ORBIT_RADIUS,
    1.5 + Math.sin(i * 1.9 + t * 0.4) * 2.2,
    Math.sin(a) * ORBIT_RADIUS,
  ];
};

/* --------------------------------------------------------- interpolation --- */

const lerp = (a, b, t) => a + (b - a) * t;

function lerp3(a, b, t, out) {
  out[0] = lerp(a[0], b[0], t);
  out[1] = lerp(a[1], b[1], t);
  out[2] = lerp(a[2], b[2], t);
  return out;
}

const scratch = {
  camPos: [0, 0, 0],
  camTarget: [0, 0, 0],
  surface: [0, 0, 0],
  accent: [0, 0, 0],
  sky: [0, 0, 0],
  skyHigh: [0, 0, 0],
  fog: [0, 0, 0],
  light: [0, 0, 0],
  decalPos: [0, 0, 0],
  decalSize: [0, 0],
};

const DEFAULT_DECAL_POS = [0, 6, -16];
const DEFAULT_DECAL_SIZE = [30, 12];

/**
 * Samples the score at a fractional act index. Reuses scratch arrays, so the
 * caller must consume the result before calling again — the render loop does.
 */
export function sampleMonolith(index) {
  const n = MONOLITH_ACTS.length;
  const clamped = Math.max(0, Math.min(n - 1, index));
  const i = Math.min(Math.floor(clamped), n - 2);
  const raw = clamped - i;
  const t = raw * raw * (3 - 2 * raw);
  const a = MONOLITH_ACTS[i];
  const b = MONOLITH_ACTS[Math.min(i + 1, n - 1)];
  const near = t < 0.5 ? a : b;

  // Only one act carries a decal at a time, and no two decal acts are adjacent,
  // so the near act always names the right texture.
  const decal = near.decal || null;
  const decalSource = a.decal ? a : b.decal ? b : null;

  return {
    indexA: i,
    indexB: Math.min(i + 1, n - 1),
    worldA: a.world,
    worldB: b.world,
    morph: t,

    camPos: lerp3(a.camera.pos, b.camera.pos, t, scratch.camPos),
    camTarget: lerp3(a.camera.target, b.camera.target, t, scratch.camTarget),
    fov: lerp(a.camera.fov, b.camera.fov, t),

    surface: lerp3(a.look.surface, b.look.surface, t, scratch.surface),
    accent: lerp3(a.look.accent, b.look.accent, t, scratch.accent),
    sky: lerp3(a.look.sky, b.look.sky, t, scratch.sky),
    skyHigh: lerp3(a.look.skyHigh, b.look.skyHigh, t, scratch.skyHigh),
    fog: lerp3(a.look.fog, b.look.fog, t, scratch.fog),
    light: lerp3(a.look.light, b.look.light, t, scratch.light),
    fogDensity: lerp(a.look.fogDensity, b.look.fogDensity, t),
    glow: lerp(a.look.glow, b.look.glow, t),
    rough: lerp(a.look.rough, b.look.rough, t),
    kaleido: lerp(a.look.kaleido, b.look.kaleido, t),

    bloom: lerp(a.grade.bloom, b.grade.bloom, t),
    aberration: lerp(a.grade.aberration, b.grade.aberration, t),
    exposure: lerp(a.grade.exposure, b.grade.exposure, t),
    vignette: lerp(a.grade.vignette, b.grade.vignette, t),

    decal,
    decalPos: lerp3(
      decalSource?.decalPos || DEFAULT_DECAL_POS,
      decalSource?.decalPos || DEFAULT_DECAL_POS,
      0,
      scratch.decalPos
    ),
    decalSize: (() => {
      const s = decalSource?.decalSize || DEFAULT_DECAL_SIZE;
      scratch.decalSize[0] = s[0];
      scratch.decalSize[1] = s[1];
      return scratch.decalSize;
    })(),
    // The panel geometry exists in both decal worlds, so fade the lettering in
    // and out with the act rather than popping it.
    decalOn: a.decal || b.decal ? 1 : 0,
  };
}
